import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToNewsletter, PENDING_NEWSLETTER_KEY } from '../utils/newsletterSignup';

export default function AuthModal({ isOpen, onClose, initialEmail = '', initialMode = 'login' }) {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, user } = useAuth();
  const [mode, setMode] = useState(initialMode); // 'login' or 'signup'
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Newsletter cross-sell (Josh 9/2): pre-checked by his call. Signup mode only.
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);

  if (!isOpen) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await signInWithEmail(email, password);
        onClose();
      } else {
        await signUpWithEmail(email, password);
        setMessage('Check your email for a confirmation link.');
        // Fire-and-forget: a newsletter hiccup must never block account creation.
        if (newsletterOptIn) subscribeToNewsletter(email).catch(() => {});
      }
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  }

  async function handleGoogle() {
    try {
      // OAuth redirects away before we know the account's email — remember the
      // ticked box; AuthContext completes the signup when the session returns.
      // The flag records WHO was signed in at click time (9/2 QA: Josh's still-
      // active previous session fired an auth event that consumed a bare flag
      // and subscribed the WRONG account before the new one returned) — it is
      // only honored for a DIFFERENT, freshly created user.
      try {
        if (mode === 'signup' && newsletterOptIn) {
          localStorage.setItem(PENDING_NEWSLETTER_KEY, JSON.stringify({ setAt: Date.now(), byUserId: user?.id || null }));
        } else {
          localStorage.removeItem(PENDING_NEWSLETTER_KEY);
        }
      } catch { /* storage unavailable — skip the cross-sell, never the signup */ }
      await signInWithGoogle();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 9999, backdropFilter: 'blur(2px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#faf7f2', padding: '28px', borderRadius: '12px', width: '90%', maxWidth: '380px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', zIndex: 10000,
      }}>
        <h2 style={{ fontFamily: "'Cinzel', serif", textAlign: 'center', marginBottom: '20px', color: '#1a1714' }}>
          {mode === 'login' ? 'Welcome Back' : 'Create Account'}
        </h2>

        {/* Above BOTH signup paths (9/2 QA: it sat inside the email form, so
            Google signups never saw it and the ticked box was silently lost). */}
        {mode === 'signup' && (
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '0 0 16px',
            fontSize: '0.85rem', color: '#6b6255', cursor: 'pointer', lineHeight: 1.4,
          }}>
            <input
              type="checkbox"
              checked={newsletterOptIn}
              onChange={e => setNewsletterOptIn(e.target.checked)}
              style={{ marginTop: '2px', accentColor: '#8b6914' }}
            />
            <span>Also send me the weekly Revival Rundown, our guide to every revival screening in NYC.</span>
          </label>
        )}

        <button
          onClick={handleGoogle}
          style={{
            width: '100%', padding: '12px', border: '1px solid #d4cec4', borderRadius: '6px',
            background: '#fff', cursor: 'pointer', fontSize: '0.95rem', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <div style={{ textAlign: 'center', color: '#6b6255', fontSize: '0.85rem', margin: '15px 0', position: 'relative' }}>
          <span style={{ background: '#faf7f2', padding: '0 10px', position: 'relative', zIndex: 1 }}>or</span>
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: '#d4cec4' }} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #d4cec4', borderRadius: '4px',
                fontSize: '0.9rem', background: '#fff', color: '#2a2520', boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={6}
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #d4cec4', borderRadius: '4px',
                fontSize: '0.9rem', background: '#fff', color: '#2a2520', boxSizing: 'border-box',
              }}
            />
          </div>

          {error && <p style={{ color: '#cc4444', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>}
          {message && <p style={{ color: '#4CAF50', fontSize: '0.85rem', marginBottom: '12px' }}>{message}</p>}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '12px', background: '#8b6914', color: '#fff', border: 'none',
              borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600',
              fontFamily: "'Inter', sans-serif", opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? '...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.85rem', color: '#6b6255' }}>
          {mode === 'login' ? (
            <>Don't have an account? <button onClick={() => { setMode('signup'); setError(''); setMessage(''); }} style={{ background: 'none', border: 'none', color: '#8b6914', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>Sign up</button></>
          ) : (
            <>Already have an account? <button onClick={() => { setMode('login'); setError(''); setMessage(''); }} style={{ background: 'none', border: 'none', color: '#8b6914', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem' }}>Sign in</button></>
          )}
        </p>
      </div>
    </div>
  );
}
